import { useEffect, useMemo, useRef, useState } from 'react'
import type { ModelResponse } from '../shared/types/Model'
import { buildModelImageGallery } from '../utils/normalize'
import './ModelCard.css'
import { MdLocationPin } from 'react-icons/md'
import { motion, AnimatePresence } from 'framer-motion'
import { MdTune } from 'react-icons/md'
import { SettingsModal } from './SettingsModal'
import type { LoginResponse } from '../shared/types/LoginResponse'
import { useTranslation } from 'react-i18next'
import { useMode } from '../shared/useMode'
import { applyModelProfile } from '../utils/modelProfiles'

export function ModelCard({
  model,
  photoIndex,
  onModelSwipe,
  controlsVisible,
  setControlsVisible,
  loginInfo,
  onLoginInfoChange,
}: {
  model: ModelResponse
  photoIndex: number
  onModelSwipe: (dir: number) => void
  controlsVisible: boolean
  setControlsVisible: React.Dispatch<React.SetStateAction<boolean>>
  loginInfo: LoginResponse | null
  onLoginInfoChange: (info: LoginResponse) => void
}) {
  const { t, i18n } = useTranslation()
  const { mode, setMode } = useMode()
  const displayModel = applyModelProfile(model, mode, i18n.resolvedLanguage ?? i18n.language)
  const images = useMemo(() => buildModelImageGallery(model, mode), [model, mode])
  // Lazy loading state
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({})
  const [loadingErrors, setLoadingErrors] = useState<Record<string, boolean>>({})

  // Implement lazy loading for images
  useEffect(() => {
    const loadImage = (src: string) => {
      // Skip if already loaded or currently loading
      if (loadedImages[src] || loadingErrors[src]) return

      const img = new Image()
      img.onload = () => {
        setLoadedImages(prev => ({ ...prev, [src]: true }))
      }
      img.onerror = () => {
        console.warn(`Failed to load image: ${src}`)
        setLoadingErrors(prev => ({ ...prev, [src]: true }))
      }
      img.src = src
    }

    // Load current and next image for better UX
    try {
      if (images.length === 0) return

      images.forEach((image) => {
        loadImage(image)
      })

      const nextImage = images[(photoIndex + 1) % images.length]
      if (nextImage) {
        loadImage(nextImage)
      }
    } catch (error) {
      console.error('Error initiating image loading:', error)
    }
  }, [images, loadedImages, loadingErrors, photoIndex])

  const [detailsVisible, setDetailsVisible] = useState(false)
  const startX = useRef<number | null>(null)
  const startY = useRef<number | null>(null)
  const lastTap = useRef<number>(0)
  const [showSettings, setShowSettings] = useState(false)
  const activeTab: 'realism' | 'roleplay' = mode
  const parsedSkills =
    (displayModel.skills ? displayModel.skills.split(',') : displayModel.interests?.split(','))
      ?.map(formatModelText)
      .filter(Boolean) ??
    []
  const skillTags = parsedSkills.slice(0, 3)
  const interestList = displayModel.interests
    ? displayModel.interests.split(',').map(formatModelText).filter(Boolean)
    : []
  const interestEmojis = ['\u270D\uFE0F', '\uD83C\uDFA8', '\uD83D\uDCD9', '\uD83C\uDFA7', '\uD83D\uDDFA\uFE0F', '\uD83C\uDF19']

  useEffect(() => {
    let hideTimer: NodeJS.Timeout

    const resetTimer = () => {
      try {
        setControlsVisible(true)
        clearTimeout(hideTimer)
        hideTimer = setTimeout(() => setControlsVisible(false), 4000)
      } catch (error) {
        console.error('Error in timer reset:', error)
      }
    }

    try {
      hideTimer = setTimeout(() => setControlsVisible(false), 4000)
      window.addEventListener('touchstart', resetTimer)
      window.addEventListener('mousemove', resetTimer)

      return () => {
        clearTimeout(hideTimer)
        window.removeEventListener('touchstart', resetTimer)
        window.removeEventListener('mousemove', resetTimer)
      }
    } catch (error) {
      console.error('Error setting up event listeners:', error)
      return () => {}
    }
  }, [setControlsVisible])

  const handleTouchStart = (e: React.TouchEvent) => {
    try {
      startX.current = e.touches[0].clientX
      startY.current = e.touches[0].clientY
    } catch (error) {
      console.error('Error handling touch start:', error)
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const resetTouch = () => {
      startX.current = null
      startY.current = null
    }

    try {
      if (startX.current === null || startY.current === null) {
        resetTouch()
        return
      }

      const changedTouch = e.changedTouches[0]
      if (!changedTouch) {
        resetTouch()
        return
      }

      const endX = changedTouch.clientX
      const endY = changedTouch.clientY
      const deltaY = endY - startY.current
      const deltaX = endX - startX.current
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)
      const isGesture = Math.max(absX, absY) > 12

      if (!isGesture) {
        const now = Date.now()
        if (now - lastTap.current < 300) {
          setDetailsVisible(true)
        }
        lastTap.current = now
        resetTouch()
        return
      }

      const isVerticalEnough = absY > 30 && absY > absX * 1.5

      if (deltaY < -30 && isVerticalEnough) {
        setDetailsVisible(true)
      } else if (deltaY > 30 && isVerticalEnough) {
        setDetailsVisible(false)
      }

      resetTouch()
    } catch (error) {
      console.error('Error handling touch end:', error)
      resetTouch()
    }
  }

  // Get current image URL with fallback for loading errors
  const getCurrentImageUrl = () => {
    try {
      const currentImage = images[photoIndex]
      if (loadingErrors[currentImage]) {
        // Fallback to a placeholder or the first successfully loaded image
        const firstLoaded = Object.keys(loadedImages).find(key => loadedImages[key])
        return firstLoaded || currentImage
      }
      return currentImage
    } catch (error) {
      console.error('Error getting current image URL:', error)
      return images[0] || ''
    }
  }

  return (
    <div
      className="card-wrapper no-scroll"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {!detailsVisible && (
      <>
          <div className="photo-header-bar">
            {mode === 'roleplay' && <span className="rp-badge">RP</span>}
            <div className="tab-switch">
              <button
                className={`tab ${activeTab === 'realism' ? 'active' : ''}`}
                onClick={() => {
                  try {
                    setMode('realism')
                  } catch (error) {
                    console.error('Error switching to realism mode:', error)
                  }
                }}
              >
                {t('tab_realistic')}
              </button>
              <button
                className={`tab ${activeTab === 'roleplay' ? 'active' : ''}`}
                onClick={() => {
                  try {
                    setMode('roleplay')
                  } catch (error) {
                    console.error('Error switching to roleplay mode:', error)
                  }
                }}
              >
                {t('tab_roleplay')}
              </button>
            </div>
            <button className="filter-button" onClick={() => setShowSettings(true)}>
              <MdTune size={22} />
            </button>
          </div>

          <div className="photo-indicators">
            {images.map((_, i) => (
              <div
                key={i}
                className={`photo-indicator ${i === photoIndex ? 'active' : ''}`}
              />
            ))}
          </div>
      </>
      )}
      
      <div
        className={`photo-area ${detailsVisible ? 'dimmed' : ''} ${
          !controlsVisible ? 'full-height' : ''
        }`}
      >
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={photoIndex}
            className="photo rounded"
            style={{
              backgroundImage: `url(${getCurrentImageUrl()})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              try {
                if (info.offset.x < -50) onModelSwipe(1)
                else if (info.offset.x > 50) onModelSwipe(-1)
              } catch (error) {
                console.error('Error during drag end:', error)
              }
            }}
            initial={{ x: 200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}

          />
        </AnimatePresence>

        <div className={`photo-overlay ${detailsVisible ? 'with-details' : ''}`}>
          <h2 className="model-name">
            <span className="model-name-bold">{displayModel.name}</span>
            <span className="model-age">, {displayModel.age}</span>
          </h2>
          <p className="model-location">
            <span className="location-icon-text">
              <MdLocationPin size={16} />
              {formatModelText(displayModel.location)}
            </span>
          </p>
          <div className="skills">
            {skillTags.map((skill, index) => (
              <span
                key={`${skill}-${index}`}
                className="skill-tag"
                onClick={() => setDetailsVisible(true)}
              >
                {skill}
              </span>
            ))}
          </div>

          {detailsVisible && (
            <div className="model-details-inline slide-up">
              <h3 className="about-me-title">{t('about_me')}</h3>
              {displayModel.description && <p className="about-me-text">{formatModelText(displayModel.description)}</p>}

              {interestList.length > 0 && (
                <div className="interests-section">
                  <h4 className="interests-title">{t('interests')}</h4>
                  <div className="interests-tags">
                    {interestList.map((interest, index) => (
                      <div key={`${interest}-${index}`} className="interest-tag">
                        <span className="interest-emoji">{interestEmojis[index] ?? '\u2728'}</span>
                        <span className="interest-text">{interest}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {displayModel.profession && (
                <div className="profession-section">
                  <h4 className="profession-title">{t('profession')}</h4>
                  <p className="profession-text">{formatModelText(displayModel.profession)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showSettings && loginInfo && (
        <SettingsModal
          data={loginInfo}
          onClose={() => setShowSettings(false)}
          onSave={onLoginInfoChange}
        />
      )}
    </div>
  )
}

function formatModelText(value: string | undefined | null) {
  if (!value) return ''

  const trimmed = value.trim()
  return trimmed.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase())
}
